import type { EmailTemplate } from '../useData';
import type { ComposeBodyDocument, ComposeQuotedBlock } from './types';
import { classifyBodyKind, htmlHasSlateRichFormatting } from './classifyBodyKind';
import {
  convertParagraphsToBodyText,
  importMessageToEditableEditorHtml,
  unwrapQuotedRichComposeHtml,
  userBodyNeedsImportUnlock,
} from '../utils/unlockReplyForwardEditing';
import { effectivePlainTextBody } from '../utils/phoenixPlaceholderPlainText';
import {
  isPlaceholderEditorHtml,
  stripTrailingSignatureBlocks,
  trimTrailingEmptyComposeParagraphs,
} from './editorHtml';
import {
  enrichQuotedBlock,
  mergeQuotedBlocks,
  normalizeQuotedBlock,
  parseOutboundQuotedBlock,
} from './quotedBlockNormalize';

function isComposeBodyDocument(value: unknown): value is ComposeBodyDocument {
  if (!value || typeof value !== 'object') return false;
  const v = value as ComposeBodyDocument;
  return (v.bodyKind === 'plain' || v.bodyKind === 'rich') && typeof v.userBody === 'string';
}

function isEmailSignatureElement(el: Element): boolean {
  const cls = el.getAttribute('class') ?? '';
  if (/\bemail-signature\b/i.test(cls)) return true;
  return Boolean(el.querySelector('.email-signature, [class*="email-signature"]'));
}

function extractTrailingSignatureFromRoot(root: HTMLElement): string | undefined {
  const parts: string[] = [];
  while (root.lastElementChild) {
    const last = root.lastElementChild;
    const tag = last.tagName.toLowerCase();
    if (tag === 'p' || tag === 'div') {
      const text = (last.textContent ?? '').replace(/\u00a0/g, ' ').trim();
      if (!text && !last.querySelector('img, table, ul, ol, blockquote, hr, a[href], pre')) {
        last.remove();
        continue;
      }
    }
    if (!isEmailSignatureElement(last)) break;
    parts.unshift(last.outerHTML);
    last.remove();
  }
  return parts.length ? parts.join('') : undefined;
}

function parseQuotedBlock(blockquote: Element): ComposeQuotedBlock {
  return parseOutboundQuotedBlock(blockquote);
}

/** Split saved template outbound HTML into user body, signature, and quoted forward/reply panel. */
export function parseSavedTemplateOutboundHtml(html: string): ComposeBodyDocument | null {
  const trimmed = html.trim();
  if (!trimmed) return null;
  const hasQuoted = trimmed.includes('email-quoted-block');
  const hasSignature = trimmed.includes('email-signature');
  if (!hasQuoted && !hasSignature) return null;
  if (typeof document === 'undefined') return null;

  const root = document.createElement('div');
  root.innerHTML = trimmed;

  let quoted: ComposeQuotedBlock | undefined;
  const blockquote = root.querySelector('blockquote.email-quoted-block');
  if (blockquote) {
    quoted = parseQuotedBlock(blockquote);
    blockquote.remove();
  }

  const signature = extractTrailingSignatureFromRoot(root);
  const userBody =
    trimTrailingEmptyComposeParagraphs(root.innerHTML.trim(), { maxTrailingEmpty: 0 }) ||
    '<div><br></div>';

  return {
    bodyKind: 'rich',
    userBody,
    signature,
    quoted,
  };
}

function normalizeLoadedOutboundComposeDoc(
  doc: ComposeBodyDocument,
  outboundHtml?: string | null,
  importSource?: { bodyHtml?: string | null; bodyText?: string | null },
): ComposeBodyDocument {
  let { userBody, signature, quoted, bodyKind } = doc;

  if (signature?.trim()) {
    userBody = stripTrailingSignatureBlocks(userBody);
  } else {
    const peeled = parseSavedTemplateOutboundHtml(userBody);
    if (peeled?.signature) {
      userBody = peeled.userBody;
      signature = peeled.signature;
      quoted = quoted ?? peeled.quoted;
    }
  }

  const nextBodyKind = 'rich';
  if (bodyKind === 'plain' && !htmlHasSlateRichFormatting(userBody)) {
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(userBody);
    userBody = importMessageToEditableEditorHtml(
      looksLikeHtml ? userBody : null,
      looksLikeHtml ? null : userBody,
    );
  } else if (bodyKind !== 'rich' && userBodyNeedsImportUnlock(userBody)) {
    // Never re-import unlock rich composeState bodies — that path can prefer plain text
    // and reflow FM:/TO: lines, stripping intentional Enter/returns.
    userBody = importMessageToEditableEditorHtml(
      importSource?.bodyHtml ?? outboundHtml ?? userBody,
      importSource?.bodyText ?? null,
    );
  }

  userBody = convertParagraphsToBodyText(unwrapQuotedRichComposeHtml(userBody));

  return {
    ...doc,
    bodyKind: nextBodyKind,
    userBody: userBody.trim() || '<div><br></div>',
    signature: signature?.trim() || undefined,
    quoted: quoted ? enrichQuotedBlock(quoted, outboundHtml) : undefined,
  };
}

function composeDocFromTemplateParts(template: {
  bodyHtml?: string | null;
  bodyText?: string | null;
  bodyKind?: 'plain' | 'rich' | null;
}): ComposeBodyDocument {
  const htmlStr = template.bodyHtml?.trim() ?? '';
  const outboundParsed = htmlStr ? parseSavedTemplateOutboundHtml(htmlStr) : null;
  if (outboundParsed) {
    return normalizeLoadedOutboundComposeDoc(outboundParsed, htmlStr, {
      bodyHtml: htmlStr,
      bodyText: template.bodyText,
    });
  }

  const bodyKind =
    template.bodyKind === 'plain' || template.bodyKind === 'rich'
      ? template.bodyKind
      : classifyBodyKind(htmlStr, effectivePlainTextBody(template.bodyText) ?? undefined);

  if (bodyKind === 'rich' && htmlStr && htmlHasSlateRichFormatting(htmlStr) && !htmlStr.includes('mso-')) {
    return normalizeLoadedOutboundComposeDoc(
      {
        bodyKind: 'rich',
        userBody: htmlStr,
      },
      htmlStr,
      { bodyHtml: htmlStr, bodyText: template.bodyText },
    );
  }

  const userBody = importMessageToEditableEditorHtml(htmlStr || null, template.bodyText);
  return normalizeLoadedOutboundComposeDoc(
    {
      bodyKind: 'rich',
      userBody: userBody.trim() || '<div><br></div>',
    },
    htmlStr,
    { bodyHtml: htmlStr, bodyText: template.bodyText },
  );
}

export function composeDocFromTemplate(template: EmailTemplate): ComposeBodyDocument {
  const templateHtml = template.bodyHtml?.trim() ?? '';
  const outboundParsed = templateHtml ? parseSavedTemplateOutboundHtml(templateHtml) : null;

  if (template.composeState && isComposeBodyDocument(template.composeState)) {
    let state = template.composeState;
    if (userBodyNeedsImportUnlock(state.userBody)) {
      state = {
        ...state,
        userBody: importMessageToEditableEditorHtml(template.bodyHtml, template.bodyText),
      };
    }
    if (!htmlHasSlateRichFormatting(state.userBody) && templateHtml) {
      if (outboundParsed && htmlHasSlateRichFormatting(outboundParsed.userBody)) {
        state = {
          ...state,
          userBody: outboundParsed.userBody,
          signature: state.signature ?? outboundParsed.signature,
          quoted:
            state.quoted && outboundParsed.quoted
              ? mergeQuotedBlocks(state.quoted, outboundParsed.quoted)
              : state.quoted ?? outboundParsed.quoted,
        };
      }
    } else if (state.quoted && outboundParsed?.quoted) {
      state = {
        ...state,
        quoted: mergeQuotedBlocks(state.quoted, outboundParsed.quoted),
      };
    }
    if (!isPlaceholderEditorHtml(state.userBody) || state.quoted || state.signature) {
      return normalizeLoadedOutboundComposeDoc(
        {
          ...state,
          bodyKind: 'rich',
        },
        templateHtml,
        { bodyHtml: template.bodyHtml, bodyText: template.bodyText },
      );
    }
  }

  return composeDocFromTemplateParts(template);
}

export { normalizeLoadedOutboundComposeDoc };
